import React from 'react';

export interface CategoryRow {
  label: string;
  count: number;
  /** highlight (e.g. a value the user's preference matches/passes). */
  highlighted?: boolean;
}

interface CategoryBarsProps {
  rows: CategoryRow[];
  /** cap the number of rows shown (rest omitted). */
  limit?: number;
  /** optional fixed max for the bar scale; defaults to the largest row. */
  max?: number;
}

/**
 * Horizontal categorical breakdown (religion, smoking, metros). Highlighted rows
 * use the accent color; the rest are muted. Presentational.
 */
export function CategoryBars({ rows, limit, max }: CategoryBarsProps) {
  const shown = limit ? rows.slice(0, limit) : rows;
  const scale = Math.max(1, max ?? Math.max(...shown.map((r) => r.count), 1));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {shown.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 110,
              flex: 'none',
              fontSize: 'var(--fs-xs)',
              color: r.highlighted ? 'var(--text)' : 'var(--text-secondary)',
              fontWeight: r.highlighted ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={r.label}
          >
            {r.label}
          </span>
          <span
            style={{
              flex: 1,
              height: 16,
              borderRadius: 4,
              background: 'var(--bg-muted)',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(2, (r.count / scale) * 100)}%`,
                background: r.highlighted
                  ? 'var(--accent)'
                  : 'var(--text-faint)',
                borderRadius: 4,
              }}
            />
          </span>
          <span
            style={{
              width: 48,
              flex: 'none',
              textAlign: 'right',
              fontSize: 'var(--fs-xs)',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text-secondary)',
            }}
          >
            {r.count.toLocaleString('en-US')}
          </span>
        </div>
      ))}
    </div>
  );
}
